import { useState, useEffect } from 'react'
import { publicCacheService, PublicKit, PublicImage } from '../utils/publicCacheService'
import KitOrderModal from '../components/KitOrderModal'

// Use public cache interface
type ProductItem = PublicKit

function BootstrapInventory() {
  const [products, setProducts] = useState<ProductItem[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string>('All')
  const [productImages, setProductImages] = useState<PublicImage[]>([])
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [selectedKit, setSelectedKit] = useState<ProductItem | null>(null)
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false)

  useEffect(() => {
    const loadPublicProducts = async () => {
      console.log('BootstrapInventory: Loading from public cache...')

      // Load active kits from public cache
      const activeProducts = await publicCacheService.getActiveKits()
      console.log('BootstrapInventory: Loaded', activeProducts.length, 'active kits from cache')
      setProducts(activeProducts)

      // Load kit images from public cache
      const images = await publicCacheService.getImagesByCategory('kits')
      console.log('BootstrapInventory: Loaded', images.length, 'kit images from cache')
      setProductImages(images)
    }

    // Initial load
    loadPublicProducts()

    // Listen for cache updates from admin panels
    const unsubscribe = publicCacheService.subscribe('cache_updated', () => {
      console.log('BootstrapInventory: Cache updated, reloading...')
      loadPublicProducts()
    })

    // Listen for server cache updates
    const handleServerCacheUpdate = () => {
      console.log('BootstrapInventory: Server cache updated, reloading...')
      loadPublicProducts()
    }
    window.addEventListener('serverCacheUpdate', handleServerCacheUpdate)

    return () => {
      unsubscribe()
      window.removeEventListener('serverCacheUpdate', handleServerCacheUpdate)
    }
  }, [])

  // Get unique categories
  const categories = ['All', ...new Set((Array.isArray(products) ? products : []).map(product => product.category))]

  // Filter products
  const filteredProducts = (Array.isArray(products) ? products : []).filter(product => {
    const categoryMatch = selectedCategory === 'All' || product.category === selectedCategory
    const searchMatch = searchTerm === '' ||
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.materials && Array.isArray(product.materials) && product.materials.some(material => material.toLowerCase().includes(searchTerm.toLowerCase())))
    // Only show kits with stock available
    const hasStock = product.stockCount > 0
    return categoryMatch && searchMatch && hasStock
  })

  const handleOrderClick = (product: ProductItem) => {
    setSelectedKit(product)
    setIsOrderModalOpen(true)
  }

  const handleOrderComplete = (orderId: string) => {
    console.log('Order completed:', orderId)
    // Modal will show confirmation automatically
  }

  const handleStockUpdate = (kitId: string, newStockCount: number) => {
    // Update the product list with new stock count and filter out items with no stock
    setProducts(prevProducts =>
      prevProducts.map(p =>
        p.id === kitId
          ? { ...p, stockCount: newStockCount, inStock: newStockCount > 0 }
          : p
      ).filter(p => p.stockCount > 0)
    )
  }

  const handleCloseOrderModal = () => {
    setIsOrderModalOpen(false)
    setSelectedKit(null)
  }

  const getProductImage = (imageId?: string): PublicImage | null => {
    if (!imageId) return null
    return productImages.find(img => img.id === imageId) || null
  }

  return (
    <div className="py-5">
      <div className="container">
        <h1 className="display-4 fw-bold text-center mb-4">Art Kits & Supplies</h1>
        <div className="row justify-content-center mb-5">
          <div className="col-lg-8">
            <div className="card shadow-lg border-0" style={{
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}>
              <div className="card-body p-4 text-center">
                <p className="h5 text-dark mb-0" style={{lineHeight: '1.6'}}>
                  Everything you need to create beautiful art! Browse our curated selection of art kits, 
                  supplies, and materials perfect for artists of all skill levels.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="row mb-5 justify-content-center">
          <div className="col-md-10">
            <div className="card shadow-sm border-0" style={{
              borderRadius: '15px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}>
              <div className="card-body p-4">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-dark">Search Products:</label>
                    <input 
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="form-control"
                      placeholder="Search by name, description, or tags..."
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-dark">Filter by Category:</label>
                    <select 
                      value={selectedCategory} 
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="form-select"
                    >
                      {categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="row g-4">
          {filteredProducts.length > 0 ? (
            filteredProducts.map((product) => {
              const productImage = getProductImage(product.imageId)
              
              return (
                <div key={product.id} className="col-lg-4 col-md-6">
                  <div className="card h-100 shadow-sm">
                    {/* Product Image */}
                    <div className="card-img-top bg-light d-flex align-items-center justify-content-center" style={{height: '200px'}}>
                      {productImage ? (
                        <img 
                          src={productImage.dataUrl} 
                          alt={product.name}
                          className="w-100 h-100 object-fit-cover"
                        />
                      ) : (
                        <div className="display-1">🎨</div>
                      )}
                    </div>

                    <div className="card-body d-flex flex-column">
                      {/* Product Header */}
                      <div className="mb-3">
                        <h5 className="card-title text-primary fw-bold">{product.name}</h5>
                        <small className="text-muted">{product.category}</small>
                      </div>

                      <p className="card-text text-muted flex-grow-1">{product.description}</p>

                      {/* Product Details */}
                      {product.difficulty && (
                        <div className="mb-2">
                          <small className="text-muted">
                            <strong>Level:</strong> {product.difficulty}
                          </small>
                        </div>
                      )}

                      {product.materials && Array.isArray(product.materials) && (
                        <div className="mb-3">
                          {product.materials.slice(0, 3).map((material, index) => (
                            <span key={index} className="badge bg-light text-dark me-1">
                              {material}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Pricing and Purchase */}
                      <div className="mt-auto">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <span className="h5 text-success fw-bold">${product.price}</span>
                            <div className="small">
                              {product.inStock ? (
                                <span className="text-success">
                                  ✅ {product.stockCount} in stock
                                </span>
                              ) : (
                                <span className="text-danger">❌ Out of stock</span>
                              )}
                            </div>
                          </div>
                          
                          <button
                            className="btn btn-warning"
                            onClick={() => handleOrderClick(product)}
                            disabled={!product.inStock}
                          >
                            <i className="fas fa-shopping-cart me-2"></i>
                            {product.inStock ? 'Order Now' : 'Out of Stock'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })
          ) : (
            <div className="col-12">
              <div className="alert alert-info text-center">
                <h5>No products match your search</h5>
                <p className="mb-0">Try adjusting your search term or category filter.</p>
              </div>
            </div>
          )}
        </div>

        {products.length === 0 && (
          <div className="text-center py-5">
            <div className="display-1 mb-4">📦</div>
            <h3 className="text-white">Products Coming Soon!</h3>
            <p className="text-white">We're stocking up on amazing art supplies for you. Check back soon!</p>
          </div>
        )}
      </div>

      {/* Order Modal */}
      {selectedKit && (
        <KitOrderModal
          kit={selectedKit}
          kitImage={getProductImage(selectedKit.imageId)}
          isOpen={isOrderModalOpen}
          onClose={handleCloseOrderModal}
          onOrderComplete={handleOrderComplete}
          onStockUpdate={handleStockUpdate}
        />
      )}
    </div>
  )
}

export default BootstrapInventory